import { FunctionPlainObject } from '../plainObjects/function.plainObject';
import { ParameterPlainObject } from '../plainObjects/parameter.plainObject';
import { TransformPlainObject } from '../plainObjects/transform.plainObject';
import { Antlr4Utils, ParsingVersion } from './antlr4-utils';
import { FunctionsVisitor } from '../visitors/functions.visitor';
import { ParametersVisitor } from '../visitors/parameters.visitor';
import { BaseTransformationVisitor } from '../visitors/baseTransformation.visitor';
import { SourcePlainObject } from '../plainObjects/source.plainObject';
import { SourceVisitor } from '../visitors/source.visitor';
import { SinkPlainObject } from '../plainObjects/sink.plainObject';
import { ColumnMapVisitor } from '../visitors/columnMap.visitor';
import { SelectPlainObject } from '../plainObjects/select.plainObject';
import { DerivePlainObject } from '../plainObjects/derive.plainObject';
import { DeriveVisitor } from '../visitors/derive.visitor';
import { AggregatePlainObject } from '../plainObjects/aggregate.plainObject';
import { WindowPlainObject } from '../plainObjects/window.plainObject';
import { PivotPlainObject } from '../plainObjects/pivot.plainObject';
import { UnpivotPlainObject } from '../plainObjects/unpivot.plainObject';
import { SurrogateKeyPlainObject } from '../plainObjects/surrogateKey.plainObject';
import { JoinPlainObject } from '../plainObjects/join.plainObject';
import { AggregateVisitor } from '../visitors/aggregate.visitor';
import { WindowVisitor } from '../visitors/window.visitor';
import { PivotVisitor } from '../visitors/pivot.visitor';
import { UnpivotVisitor } from '../visitors/unpivot.visitor';
import { SurrogateKeyVisitor } from '../visitors/surrogateKey.Visitor';
import { JoinVisitor } from '../visitors/join.visitor';
import { ExistsPlainObject } from '../plainObjects/exists.plainObject';
import { UnionPlainObject } from '../plainObjects/union.plainObject';
import { UnionVisitor } from '../visitors/union.visitor';
import { ConditionalSplitPlainObject } from '../plainObjects/conditionalSplit.plainObject';
import { ConditionalSplitVisitor } from '../visitors/conditionalSplit.visitor';
import { FilterPlainObject } from '../plainObjects/filter.plainObject';
import { FilterVisitor } from '../visitors/filter.visitor';
import { ExistsVisitor } from '../visitors/exists.visitor';
import { SortPlainObject } from '../plainObjects/sort.plainObject';
import { SortVisitor } from '../visitors/sort.visitor';
import { AlterRowPlainObject } from '../plainObjects/alterRow.plainObject';
import { LookupPlainObject } from '../plainObjects/lookup.plainObject';
import { LookupVisitor } from '../visitors/lookup.visitor';
import { ExtendPlainObject } from '../plainObjects/extend.plainObject';
import { ExtendVisitor } from '../visitors/extend.visitor';
import { AlterRowVisitor } from '../visitors/alterRow.visitor';
import { SinkVisitor } from '../visitors/sink.visitor';
import { FoldDownPlainObject } from '../plainObjects/folddown.plainObject';
import { FoldDownVisitor } from '../visitors/folddown.visitor';
import { RankPlainObject } from '../plainObjects/rank.plainObject';
import { ParsePlainObject } from '../plainObjects/parse.plainObject';
import { RankVisitor } from '../visitors/rank.visitor';
import { ParseVisitor } from '../visitors/parse.visitor';
import { ExecuteFlowletPlainObject } from '../plainObjects/executeFlowlet.plainObject';
import { ExecuteFlowletVisitor } from '../visitors/executeFlowlet.visitor';
import { AssertPlainObject } from '../plainObjects/assert.plainObject';
import { AssertVisitor } from '../visitors/assert.visitor';
import { InputPlainObject } from '../plainObjects/input.plainObject';
import { OutputPlainObject } from '../plainObjects/output.plainObject';
import { InputVisitor } from '../visitors/input.visitor';
import { OutputVisitor } from '../visitors/output.visitor';
import { StringifyPlainObject } from '../plainObjects/stringify.plainObject';
import { StringifyVisitor } from '../visitors/stringify.visitor';
import { GuardVisitor } from '../visitors/guard.visitor';
import { CallPlainObject } from '../plainObjects/call.plainObject';
import { CallVisitor } from '../visitors/call.visitor';
import { CastPlainObject } from '../plainObjects/cast.plainObject';
import { CastVisitor } from '../visitors/cast.visitor';

const antlr4 = require('antlr4');

export interface IDataFlowPOParserOptions {
    // we should avoid using ts enum, as these might be used across npm modules causing problems
    parserVersion: 'vStable' | 'v1' | 'v2';
}

export interface IDataFlowPlainObjects {
    functions: FunctionPlainObject[];
    parameters: ParameterPlainObject[];
    transformations: TransformPlainObject[];
}

export class DataFlowPlainObjectParser {

    public parseDataFlowScript(flowScript: string, option?: Partial<IDataFlowPOParserOptions>): IDataFlowPlainObjects {

        let currentParserVersion: ParsingVersion = ParsingVersion.StableVersion;

        if (option?.parserVersion) {
            switch (option.parserVersion) {
                case 'v1':
                    currentParserVersion = ParsingVersion.OriginalVersion;
                    break;
                case 'v2':
                    currentParserVersion = ParsingVersion.EnhancedWithAnnotation;
                    break;
                default:
                    break;
            }
        }


        const tokenStream = Antlr4Utils.getTokenStream(flowScript, undefined, currentParserVersion);
        const parser = Antlr4Utils.getParser(tokenStream, undefined, currentParserVersion);

        parser._interp.predictionMode = antlr4.atn.PredictionMode.SLL;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let dataflowStatement: any;
        try {
            dataflowStatement = parser.dataflow();
        } catch {
            tokenStream.reset();
            parser.reset();
            parser._interp.predictionMode = antlr4.atn.PredictionMode.LL;
            dataflowStatement = parser.dataflow();
        }
        const functionsStatement = dataflowStatement.functions();
        const parametersStatement = dataflowStatement.parameters();
        const flowStatement = dataflowStatement.flowStatement();
        const scriptProperties: IDataFlowPlainObjects = {
            functions: [],
            parameters: [],
            transformations: []
        };
        try {
            if (functionsStatement) {
                const functionsVisitor = new FunctionsVisitor(tokenStream, scriptProperties.functions);
                functionsVisitor.visit(functionsStatement);
            }
            if (parametersStatement) {
                const parametersVisitor = new ParametersVisitor(tokenStream, scriptProperties.parameters);
                parametersVisitor.visit(parametersStatement);
            }
            if (flowStatement) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                flowStatement.forEach((transformationStatement: any) => {
                    const transformName = transformationStatement.transformation().transformName().getText();
                    const visitor = this._getTransformationConvertor(transformName, tokenStream, scriptProperties.transformations);
                    if (visitor) {
                        visitor.visit(transformationStatement);
                    }
                });
            }

            return scriptProperties;
        } catch (error) {
            // console.error('DataflowScriptParser', `Unable to walk through the parsed tree fully for dataflow ${dataflowStatement.name}'s script ${flowScript}`, error);
            error.message = 'Not able to parse script ' + error.message;
            throw error;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _getTransformationConvertor(transformName: string, tokenStream: any, transformations: TransformPlainObject[]): BaseTransformationVisitor {
        let transformationObject: TransformPlainObject;
        let visitor: BaseTransformationVisitor;

        if (transformName === 'filter') {
            transformName = 'filtertransformation';
        } else if (transformName === 'lookup') {
            transformName = 'lookuptransformation';
        }

        switch (transformName) {
            case 'source':
                transformationObject = new SourcePlainObject(transformName);
                visitor = new SourceVisitor(tokenStream, transformationObject);
                break;
            case 'sink':
                transformationObject = new SinkPlainObject(transformName);
                visitor = new SinkVisitor(tokenStream, transformationObject);
                break;
            case 'select':
                transformationObject = new SelectPlainObject(transformName);
                visitor = new ColumnMapVisitor(tokenStream, transformationObject);
                break;
            case 'derive':
                transformationObject = new DerivePlainObject(transformName);
                visitor = new DeriveVisitor(tokenStream, transformationObject);
                break;
            case 'aggregate':
                transformationObject = new AggregatePlainObject(transformName);
                visitor = new AggregateVisitor(tokenStream, transformationObject);
                break;
            case 'window':
                transformationObject = new WindowPlainObject(transformName);
                visitor = new WindowVisitor(tokenStream, transformationObject);
                break;
            case 'pivot':
                transformationObject = new PivotPlainObject(transformName);
                visitor = new PivotVisitor(tokenStream, transformationObject);
                break;
            case 'unpivot':
                transformationObject = new UnpivotPlainObject(transformName);
                visitor = new UnpivotVisitor(tokenStream, transformationObject);
                break;
            case 'keyGenerate':
                transformationObject = new SurrogateKeyPlainObject(transformName);
                visitor = new SurrogateKeyVisitor(tokenStream, transformationObject);
                break;
            case 'join':
                transformationObject = new JoinPlainObject(transformName);
                visitor = new JoinVisitor(tokenStream, transformationObject);
                break;
            case 'exists':
                transformationObject = new ExistsPlainObject(transformName);
                visitor = new ExistsVisitor(tokenStream, transformationObject);
                break;
            case 'union':
                transformationObject = new UnionPlainObject(transformName);
                visitor = new UnionVisitor(tokenStream, transformationObject);
                break;
            case 'split':
                transformationObject = new ConditionalSplitPlainObject(transformName);
                visitor = new ConditionalSplitVisitor(tokenStream, transformationObject);
                break;
            case 'filtertransformation':
                transformationObject = new FilterPlainObject(transformName);
                visitor = new FilterVisitor(tokenStream, transformationObject);
                break;
            case 'sort':
                transformationObject = new SortPlainObject(transformName);
                visitor = new SortVisitor(tokenStream, transformationObject);
                break;
            case 'rank':
                transformationObject = new RankPlainObject(transformName);
                visitor = new RankVisitor(tokenStream, transformationObject);
                break;
            case 'parse':
                transformationObject = new ParsePlainObject(transformName);
                visitor = new ParseVisitor(tokenStream, transformationObject);
                break;
            case 'alterRow':
                transformationObject = new AlterRowPlainObject(transformName);
                visitor = new AlterRowVisitor(tokenStream, transformationObject);
                break;
            case 'extend':
                transformationObject = new ExtendPlainObject(transformName);
                visitor = new ExtendVisitor(tokenStream, transformationObject);
                break;
            case 'lookuptransformation':
                transformationObject = new LookupPlainObject(transformName);
                visitor = new LookupVisitor(tokenStream, transformationObject);
                break;
            case 'foldDown':
                transformationObject = new FoldDownPlainObject(transformName);
                visitor = new FoldDownVisitor(tokenStream, transformationObject);
                break;
            case 'compose':
                transformationObject = new ExecuteFlowletPlainObject(transformName);
                visitor = new ExecuteFlowletVisitor(tokenStream, transformationObject);
                break;
            case 'assert':
                transformationObject = new AssertPlainObject(transformName);
                visitor = new AssertVisitor(tokenStream, transformationObject);
                break;
            case 'input':
                transformationObject = new InputPlainObject(transformName);
                visitor = new InputVisitor(tokenStream, transformationObject);
                break;
            case 'output':
                transformationObject = new OutputPlainObject(transformName);
                visitor = new OutputVisitor(tokenStream, transformationObject);
                break;
            case 'stringify':
                transformationObject = new StringifyPlainObject(transformName);
                visitor = new StringifyVisitor(tokenStream, transformationObject);
                break;
            case 'guard':
                transformationObject = new TransformPlainObject(transformName);
                visitor = new GuardVisitor(tokenStream, transformationObject);
                break;
            case 'call':
                transformationObject = new CallPlainObject(transformName);
                visitor = new CallVisitor(tokenStream, transformationObject);
                break;
            case 'cast':
                transformationObject = new CastPlainObject(transformName);
                visitor = new CastVisitor(tokenStream, transformationObject);
                break;
            default:
                return undefined;
        }

        transformations.push(transformationObject);
        return visitor;
    }
}
